// 원장 호출 — 원장 응답 보기 (1차 웹, 명세 §3). 원장실 PC 브라우저 탭 또는 원장 휴대폰에서 연다
//  - 미응답 목록(급함 > 마취 경과 긴 순 > 대기 긴 순), 각 줄 [가는 중]/[2분]/[5분](또는 1·3·5분)
//  - 재실/부재 토글, 데스크 기록(전화함·방문객 있음) 한 줄 표시
//  - 새 호출 = 소리(Web Audio — 첫 클릭 [소리 켜기]로 허용) + 브라우저 알림(권한 있을 때). 미응답 soundUpSec 경과 = 소리 한 단계 크게, 10초마다 반복
//  - 알림·소리 문구 = 체어 번호·상황뿐(환자 정보 없음)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ackOptions, sortForDoctor, toMs, fmtMSS, situationColor, situationLabel, callText, ackRemainMs } from '../../../utils/doctorCall';

const REPEAT_MS = 10000;
const SOUND_PREF_KEY = 'dentcluster.doctorCall.sound';   // 'off' 면 이 기기에서 소리 끔(기본 켜짐)

function DoctorCallDoctorView({ settings, calls, present, nowMs, loaded, disabled, onAck, onPresent, compact }) {
    const pending = useMemo(() => sortForDoctor(calls.filter(c => c.state === 'open')), [calls]);
    const acked = useMemo(() => calls.filter(c => c.state === 'acked'), [calls]);
    const opts = ackOptions(settings.ackSet);

    // ── 소리: 기본 켜짐(대표 10-08 "소리는 자동으로 켜지게"). 끄기는 이 기기에만 기억(localStorage).
    //  브라우저 자동재생 정책상 페이지를 연 뒤 사용자 동작(클릭·터치·키) 전에는 소리가 막힐 수 있다 →
    //  들어오자마자 AudioContext 를 만들고, 막혀 있으면(suspended) 화면 어디든 첫 동작에서 자동으로 풀어 준다(버튼 불필요).
    const audioRef = useRef(null);
    const [soundOn, setSoundOn] = useState(() => {
        try { return window.localStorage.getItem(SOUND_PREF_KEY) !== 'off'; } catch (e) { console.warn('[원장 호출] 소리 설정 읽기 실패:', e?.message || e); return true; }
    });
    const [audioState, setAudioState] = useState('none');   // none | suspended | running
    const [soundErr, setSoundErr] = useState('');
    const ensureAudio = useCallback(() => {
        try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) { setSoundErr('이 브라우저는 소리를 낼 수 없습니다.'); return null; }
            if (!audioRef.current) {
                audioRef.current = new AC();
                audioRef.current.onstatechange = () => setAudioState(audioRef.current ? audioRef.current.state : 'none');
            }
            setAudioState(audioRef.current.state);
            return audioRef.current;
        } catch (e) {
            console.error('[원장 호출] 소리 준비 실패:', e?.message || e);
            setSoundErr('소리를 켜지 못했습니다. 브라우저 설정을 확인해 주세요.');
            return null;
        }
    }, []);
    const beep = useCallback((loud) => {
        const ctx = audioRef.current;
        if (!ctx) return;
        try {
            if (ctx.state === 'suspended') ctx.resume().catch(e => console.error('[원장 호출] 소리 재개 실패:', e?.message || e));
            const t0 = ctx.currentTime;
            [0, 0.28].forEach((dt, i) => {
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.type = 'sine';
                o.frequency.value = i ? 1046 : 880;
                g.gain.setValueAtTime(0.0001, t0 + dt);
                g.gain.exponentialRampToValueAtTime(loud ? 0.7 : 0.25, t0 + dt + 0.02);
                g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.24);
                o.connect(g); g.connect(ctx.destination);
                o.start(t0 + dt); o.stop(t0 + dt + 0.26);
            });
        } catch (e) {
            console.error('[원장 호출] 소리 재생 실패:', e?.message || e);
        }
    }, []);
    // 들어오자마자 준비 + 막혀 있으면 화면 첫 동작에서 자동 해제
    useEffect(() => {
        if (!soundOn) return undefined;
        const ctx = ensureAudio();
        if (!ctx || ctx.state !== 'suspended') return undefined;
        const unlock = () => {
            ctx.resume().then(() => setAudioState(ctx.state)).catch(e => console.error('[원장 호출] 소리 자동 해제 실패:', e?.message || e));
        };
        const evs = ['pointerdown', 'keydown', 'touchstart'];
        evs.forEach(ev => document.addEventListener(ev, unlock, { once: true, capture: true }));
        return () => evs.forEach(ev => document.removeEventListener(ev, unlock, { capture: true }));
    }, [soundOn, ensureAudio]);
    const setSoundPref = (on) => {
        try { window.localStorage.setItem(SOUND_PREF_KEY, on ? 'on' : 'off'); } catch (e) { console.warn('[원장 호출] 소리 설정 저장 실패:', e?.message || e); }
        setSoundOn(on); setSoundErr('');
        if (on) { const ctx = ensureAudio(); if (ctx) beep(false); }   // 켜기 버튼 = 사용자 동작이라 바로 시험음
    };
    useEffect(() => () => {
        if (audioRef.current) audioRef.current.close().catch(e => console.error('[원장 호출] 소리 닫기 실패:', e?.message || e));
    }, []);

    // ── 브라우저 알림 권한
    const notifSupported = typeof window !== 'undefined' && 'Notification' in window;
    const [notifPerm, setNotifPerm] = useState(notifSupported ? window.Notification.permission : 'unsupported');
    const askNotif = () => {
        if (!notifSupported) return;
        try {
            const p = window.Notification.requestPermission();
            if (p && typeof p.then === 'function') {
                p.then(setNotifPerm).catch(e => console.error('[원장 호출] 알림 권한 요청 실패:', e?.message || e));
            }
        } catch (e) {
            console.error('[원장 호출] 알림 권한 요청 실패:', e?.message || e);
        }
    };

    // ── 새 호출 감지 → 소리 + 알림 (처음 불러온 목록은 알리지 않음)
    //  기준선 = 구독이 처음 도착한(loaded) 뒤의 목록 — 로딩 전 빈 목록으로 기준을 잡으면 새로고침 때 기존 호출을 전부 다시 알린다
    const seenRef = useRef(null);
    useEffect(() => {
        if (!loaded) return;
        const ids = pending.map(c => c.id);
        if (seenRef.current === null) { seenRef.current = new Set(ids); return; }
        const fresh = pending.filter(c => !seenRef.current.has(c.id));
        ids.forEach(id => seenRef.current.add(id));
        if (!fresh.length) return;
        if (soundOn) beep(fresh.some(c => c.situation === 'urgent'));
        if (notifSupported && window.Notification.permission === 'granted') {
            fresh.forEach(c => {
                try {
                    // eslint-disable-next-line no-new
                    new window.Notification('원장 호출', { body: callText(c, settings), tag: `doctorCall_${c.id}`, requireInteraction: c.situation === 'urgent' });
                } catch (e) {
                    console.error('[원장 호출] 브라우저 알림 실패:', e?.message || e);
                }
            });
        }
    }, [pending, loaded, soundOn, beep, notifSupported, settings]);

    // ── 미응답 soundUpSec 경과 → 큰 소리 10초마다
    const lastLoudRef = useRef(0);
    useEffect(() => {
        if (!soundOn) return;
        const overdue = pending.some(c => {
            const w = nowMs - toMs(c.createdAt);
            return Number.isFinite(w) && w >= settings.soundUpSec * 1000;
        });
        if (overdue && Date.now() - lastLoudRef.current >= REPEAT_MS) {
            lastLoudRef.current = Date.now();
            beep(true);
        }
    }, [nowMs, pending, soundOn, settings.soundUpSec, beep]);

    const deskLine = (c) => {
        const parts = [];
        if (c.deskVisitorAt) parts.push('방문객 있음');
        if (c.deskCalledAt) parts.push('원장실로 전화함');
        if (!parts.length) return null;
        return <div style={V.desk}>{`데스크: ${parts.join(' · ')}`}</div>;
    };

    return (
        <div style={{ display: 'grid', gap: compact ? 8 : 12 }}>
            <div style={V.bar}>
                <span style={V.label}>{'원장실'}</span>
                <button type="button" disabled={disabled} onClick={() => onPresent(true)}
                    style={{ ...V.btn, ...(present === true ? V.btnHere : null) }} aria-pressed={present === true}>
                    {'재실'}
                </button>
                <button type="button" disabled={disabled} onClick={() => onPresent(false)}
                    style={{ ...V.btn, ...(present === false ? V.btnAway : null) }} aria-pressed={present === false}>
                    {'부재'}
                </button>
                {present === null && <span style={V.muted}>{'재실/부재 미설정'}</span>}
                {!soundOn
                    ? <button type="button" onClick={() => setSoundPref(true)} style={V.btn}>{'소리 켜기'}</button>
                    : <>
                        <span style={audioState === 'running' ? V.ok : V.muted}>{audioState === 'running' ? '소리 켜짐' : '소리 대기'}</span>
                        <button type="button" onClick={() => setSoundPref(false)} style={V.btn}>{'소리 끄기'}</button>
                    </>}
                {notifSupported && notifPerm === 'default' && <button type="button" onClick={askNotif} style={V.btn}>{'브라우저 알림 허용'}</button>}
                {notifPerm === 'granted' && <span style={V.ok}>{'브라우저 알림 켜짐'}</span>}
                {notifPerm === 'denied' && <span style={V.muted}>{'브라우저 알림이 차단되어 있습니다(브라우저 설정에서 허용)'}</span>}
                {soundErr && <span style={V.err}>{soundErr}</span>}
            </div>
            {!soundOn && <div style={V.hint}>{'이 기기에서 소리가 꺼져 있습니다. [소리 켜기]를 누르면 다시 켜집니다.'}</div>}
            {soundOn && audioState === 'suspended' && <div style={V.hint}>{'브라우저 규칙상 화면을 한 번 누르면 소리가 켜집니다. 아무 곳이나 한 번 눌러 주세요.'}</div>}

            <div>
                <div style={V.grp}>{`응답 기다리는 호출 ${pending.length}건`}</div>
                {pending.length === 0 && <div style={V.empty}>{'지금 기다리는 호출이 없습니다.'}</div>}
                {pending.map(c => {
                    const color = situationColor(c.situation, settings);
                    const w = nowMs - toMs(c.createdAt);
                    const over = Number.isFinite(w) && w >= settings.soundUpSec * 1000;
                    // 미응답 = 상황색으로 1초 간격 깜빡임(시안 ① 원장실 팝업). soundUpSec 경과 후엔 더 진하게
                    const blinkOn = Number.isFinite(w) && Math.floor(w / 1000) % 2 === 0;
                    const blinkBg = blinkOn ? `${color}${over ? '40' : '22'}` : '#fff';
                    return (
                        <div key={c.id} style={{ ...V.row, borderLeftColor: color, borderColor: color, background: blinkBg }}>
                            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                                <div style={{ fontSize: compact ? 15 : 19, fontWeight: 800, color: '#17201d' }}>
                                    <span style={{ color }}>{'● '}</span>{`${c.chairNo}번 체어 · ${situationLabel(c.situation, settings)}`}
                                </div>
                                <div style={V.time}>{`대기 ${fmtMSS(Number.isFinite(w) ? w : 0)}`}</div>
                                {deskLine(c)}
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {opts.map(o => (
                                    <button key={o.key} type="button" disabled={disabled}
                                        onClick={() => onAck(c, o.key)} style={{ ...V.ack, borderColor: color, color }}>
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {acked.length > 0 && (
                <div>
                    <div style={V.grp}>{'응답함'}</div>
                    {acked.map(c => {
                        const remain = ackRemainMs(c, nowMs);
                        return (
                            <div key={c.id} style={{ ...V.row, borderLeftColor: '#d9dfdc', opacity: 0.8 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#17201d' }}>{`${c.chairNo}번 체어 · ${situationLabel(c.situation, settings)}`}</div>
                                    <div style={V.time}>{remain === null ? '가는 중' : `남은 시간 ${fmtMSS(remain)}`}</div>
                                    {deskLine(c)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const V = {
    bar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    btn: { border: '1px solid #d9dfdc', background: '#fff', color: '#17201d', borderRadius: 8, padding: '8px 14px', fontSize: 14, cursor: 'pointer', fontWeight: 600, minHeight: 40 },
    label: { fontSize: 13, fontWeight: 700, color: '#46524e' },
    btnHere: { borderColor: '#1A8F6F', background: '#1A8F6F', color: '#fff' },
    btnAway: { borderColor: '#6b7280', background: '#e5e7eb', color: '#374151' },
    ok: { fontSize: 12.5, color: '#1A8F6F', fontWeight: 600 },
    muted: { fontSize: 12.5, color: '#7a8783' },
    err: { fontSize: 12.5, color: '#b3261e', fontWeight: 600 },
    hint: { fontSize: 12.5, color: '#9a5b00', background: '#fff3dc', borderRadius: 7, padding: '7px 10px' },
    grp: { fontSize: 12, fontWeight: 700, color: '#7a8783', margin: '4px 2px 6px' },
    empty: { fontSize: 13.5, color: '#7a8783', padding: '14px 4px' },
    row: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', border: '1px solid #d9dfdc', borderLeft: '6px solid', borderRadius: 8, marginBottom: 8, background: '#fff' },
    time: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13.5, color: '#46524e', fontVariantNumeric: 'tabular-nums', marginTop: 2 },
    desk: { fontSize: 12.5, color: '#9a5b00', fontWeight: 700, marginTop: 3 },
    ack: { minWidth: 64, minHeight: 48, border: '2px solid', background: '#fff', borderRadius: 10, fontSize: 16, fontWeight: 800, cursor: 'pointer', padding: '0 12px' },
};

export default DoctorCallDoctorView;
